import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, TextField, CircularProgress,
  Alert, IconButton, Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  FlightTakeoff as TripIcon,
  LocationOn as LocationIcon,
  Schedule as DurationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

const CreateTripFromEventDialog = ({ open, onClose, guide }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(guide?.title || '');
  const [startDate, setStartDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const getEndDate = () => {
    if (!startDate || !guide?.recommended_days) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + guide.recommended_days - 1);
    return end.toISOString().split('T')[0];
  };

  const handleCreate = async () => {
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const response = await api.post(`/event-guides/${guide.id}/create-trip`, {
        startDate,
        title: title.trim() || guide.title,
      });

      if (response.data.success) {
        setSuccess(response.data);
      }
    } catch (err) {
      console.error('Create trip error:', err);
      if (err.response?.status === 401) {
        setError('Please log in to create a trip.');
      } else {
        setError(err.response?.data?.error || 'Failed to create trip. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleViewTrip = () => {
    if (success?.trip?.id) {
      navigate(`/trips/${success.trip.id}`);
    }
    onClose();
  };

  const handleClose = () => {
    setError('');
    setSuccess(null);
    onClose();
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TripIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Plan This Trip
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Trip Created!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your trip has been created with activities from this guide. You can now customize it.
            </Typography>
            {success.trip?.itemCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {success.trip.itemCount} activities added to your itinerary
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleViewTrip}
              startIcon={<TripIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              View My Trip
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Guide Summary */}
            <Box sx={{
              p: 2, borderRadius: 2, bgcolor: 'grey.50',
              border: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {guide?.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {guide?.location_name && (
                  <Chip
                    icon={<LocationIcon sx={{ fontSize: 14 }} />}
                    label={guide.location_name}
                    size="small" variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
                <Chip
                  icon={<DurationIcon sx={{ fontSize: 14 }} />}
                  label={`${guide?.recommended_days || 1} ${(guide?.recommended_days || 1) === 1 ? 'day' : 'days'}`}
                  size="small" variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary">
              This will create a new trip with activities from this guide pre-loaded into your itinerary. You can customize everything after creation.
            </Typography>

            <TextField
              label="Trip Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              placeholder={guide?.title}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label="When does your trip start?"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {startDate && (
              <Box sx={{
                p: 1.5, borderRadius: 2, bgcolor: 'primary.50',
                border: '1px solid', borderColor: 'primary.100',
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Your trip will be from{' '}
                  <strong>
                    {new Date(startDate).toLocaleDateString()} — {new Date(getEndDate()).toLocaleDateString()}
                  </strong>{' '}
                  ({guide?.recommended_days || 1} {(guide?.recommended_days || 1) === 1 ? 'day' : 'days'})
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} /> : <TripIcon />}
            onClick={handleCreate}
            disabled={creating || !startDate}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {creating ? 'Creating...' : 'Create My Trip'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateTripFromEventDialog;
